import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, UserPlusIcon, UsersIcon } from '@heroicons/react/24/outline';
import { HouseholdAggregate } from '../domain';
import { useAuth } from '../hooks/useAuth';
import { useHouseholdCard } from '../hooks/useHouseholdCard';
import InviteMemberModal from './InviteMemberModal';
import MembersDialog from './MembersDialog';
import { ActionMenu, AvatarStack, Badge, Button, Card, ConfirmDialog, cx } from './ui';

interface HouseholdCardProps {
  household: HouseholdAggregate;
}

const HouseholdCard: React.FC<HouseholdCardProps> = ({ household: aggregate }) => {
  const { household } = aggregate;
  const { isLocal } = useAuth();
  const navigate = useNavigate();
  const {
    isOwner,
    since,
    members,
    choreCount,
    overdueCount,
    isDeleteConfirmOpen,
    isDeleting,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmRemove,
    isInviteModalOpen,
    openInviteModal,
    closeInviteModal,
    isMembersDialogOpen,
    openMembersDialog,
    closeMembersDialog,
  } = useHouseholdCard(aggregate);

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-50 text-lg font-semibold uppercase text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
        >
          {household.name.trim().charAt(0)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">{household.name}</h3>
            {isOwner && <Badge tone="brand" size="sm">Owner</Badge>}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Since {since}</p>
        </div>
        {isOwner && (
          <ActionMenu
            label={`More actions for ${household.name}`}
            items={[
              // On the phone alone there's no one else to be a member.
              ...(isLocal ? [] : [{ label: 'Members…', icon: UsersIcon, onClick: openMembersDialog }, 'separator' as const]),
              { label: 'Delete…', icon: TrashIcon, danger: true, onClick: openDeleteConfirm },
            ]}
          />
        )}
      </div>

      <dl className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/60">
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Chores</dt>
          <dd className="text-lg font-semibold text-gray-900 dark:text-gray-100">{choreCount}</dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500 dark:text-gray-400">Overdue</dt>
          <dd className={cx('text-lg font-semibold', overdueCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
            {overdueCount}
          </dd>
        </div>
      </dl>

      <div className="mt-auto flex items-center justify-between gap-3">
        <AvatarStack people={members} />
        {isOwner && (
          isLocal
            ? <Button size="sm" icon={UserPlusIcon} onClick={() => navigate('/login')}>Sign in to share</Button>
            : <Button size="sm" icon={UserPlusIcon} onClick={openInviteModal}>Invite</Button>
        )}
      </div>

      {!isLocal && isOwner && (
        <MembersDialog household={household} isOpen={isMembersDialogOpen} onClose={closeMembersDialog} />
      )}

      {!isLocal && (
        <InviteMemberModal
          householdId={household.id}
          householdName={household.name}
          isOpen={isInviteModalOpen}
          onClose={closeInviteModal}
        />
      )}

      <ConfirmDialog
        open={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmRemove}
        title={`Delete ${household.name}?`}
        message="The household and all its chores will be deleted for every member. This can't be undone."
        confirmLabel="Delete household"
        tone="danger"
        loading={isDeleting}
      />
    </Card>
  );
};

export default HouseholdCard;
